import React, { useState, useEffect, useRef } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { TABLES } from '../../../../../constants';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../../../utils/errorLogging';
import {
  createAutoProjectItem,
  autoCreateCeilingGeves,
  MASTER_ITEM_IDS,
} from '../../../../../utils/autoProjectItems';
import { formatFloorName } from '../../../../../utils/formatters';
import styles from '../../WizardSteps.module.css';
import spacesStyles from './SpacesStep.module.css';

// Empty space form template
const createEmptySpace = floorId => ({
  id: `space-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  floor_id: floorId,
  master_space_id: '',
  custom_space_name: '',
  room_size: null,
  room_height: null,
  description: '',
});

const SpacesStep = ({
  data,
  onChange,
  floorsData,
  spaceTypes = [],
  projectId,
  savedFloorIds = {},
  extractedSpaces = {},
}) => {
  const supabase = useSupabaseClient();
  const { logError } = useErrorLogging();
  const [spaces, setSpaces] = useState(data?.spaces || []);
  const [expandedFloors, setExpandedFloors] = useState({});
  const [editingSpace, setEditingSpace] = useState(null); // { floorId, space } or null
  const [formData, setFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [addDoor, setAddDoor] = useState(false);
  const [addCeilingGeves, setAddCeilingGeves] = useState(false);
  const [ceilingGevesQty, setCeilingGevesQty] = useState(null);
  const hasAutoSaved = useRef(false);
  // Unmatched spaces from AI that need user classification
  const [unmatchedSpaces, setUnmatchedSpaces] = useState([]);

  // Auto-save extracted spaces from floor plan analysis on mount
  // Matched spaces (with master_space_id) are saved to DB automatically.
  // Unmatched spaces (no master_space_id) are shown to the user for classification.
  useEffect(() => {
    if (hasAutoSaved.current) return;
    if (!extractedSpaces || Object.keys(extractedSpaces).length === 0) return;
    if (!projectId || Object.keys(savedFloorIds).length === 0) return;
    // Don't auto-save if we already have spaces (e.g. returning user)
    if (data?.spaces?.length > 0) return;

    hasAutoSaved.current = true;

    const autoSaveExtractedSpaces = async () => {
      setIsSaving(true);
      const savedSpaces = [];
      const pendingUnmatched = [];

      try {
        // Build insert rows from all floors' extracted spaces
        const insertRows = [];
        const localSpaceRefs = []; // Track local IDs for state update

        for (const [localFloorId, floorSpaces] of Object.entries(extractedSpaces)) {
          const dbFloorId = savedFloorIds[localFloorId];
          if (!dbFloorId) continue;

          for (const space of floorSpaces) {
            if (!space.master_space_id) {
              // Collect unmatched spaces for user classification
              pendingUnmatched.push({
                id: `unmatched-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                localFloorId,
                dbFloorId,
                suggested_name: space.suggested_name || '',
                suggested_name_he: space.suggested_name_he || space.suggested_name || '',
                estimated_size_sqm: space.estimated_size_sqm || null,
                confidence: space.confidence || 0,
                selectedMasterSpaceId: '', // User will pick this
              });
              continue;
            }

            const localId = `space-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            insertRows.push({
              floor_id: dbFloorId,
              master_space_id: space.master_space_id,
              custom_space_name: space.suggested_name_he || space.suggested_name || null,
              room_size: space.estimated_size_sqm || null,
              room_height: null,
              description: null,
            });
            localSpaceRefs.push({ localId, localFloorId, space });
          }
        }

        // Save unmatched spaces for user classification
        if (pendingUnmatched.length > 0) {
          setUnmatchedSpaces(pendingUnmatched);
        }

        if (insertRows.length > 0) {
          // Batch insert matched spaces
          const { data: dbSpaces, error } = await supabase
            .from(TABLES.PROJECT_SPACES)
            .insert(insertRows)
            .select();

          if (error) throw error;

          // Build local state from DB results
          if (dbSpaces) {
            for (let i = 0; i < dbSpaces.length; i++) {
              const dbSpace = dbSpaces[i];
              const ref = localSpaceRefs[i];
              savedSpaces.push({
                id: ref.localId,
                dbId: dbSpace.id,
                floor_id: ref.localFloorId,
                master_space_id: dbSpace.master_space_id,
                custom_space_name: dbSpace.custom_space_name || '',
                room_size: dbSpace.room_size,
                room_height: dbSpace.room_height,
                description: dbSpace.description || '',
              });
            }
          }
        }

        setSpaces(savedSpaces);
      } catch (err) {
        console.error('Error auto-saving extracted spaces:', err);
        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA,
          message: 'Failed to auto-save extracted spaces',
          metadata: { projectId },
        });
        setLocalError('שגיאה בשמירת החללים שזוהו');
      } finally {
        setIsSaving(false);
      }
    };

    autoSaveExtractedSpaces();
  }, [extractedSpaces, projectId, savedFloorIds, data?.spaces, supabase, logError]);

  // Initialize all floors as expanded
  useEffect(() => {
    if (floorsData?.floors) {
      const expanded = {};
      floorsData.floors.forEach(floor => {
        expanded[floor.id] = true;
      });
      setExpandedFloors(expanded);
    }
  }, [floorsData?.floors]);

  // Update parent when spaces change
  useEffect(() => {
    onChange({ spaces });
  }, [spaces, onChange]);

  // Get spaces for a specific floor
  const getSpacesForFloor = floorId => {
    return spaces.filter(s => s.floor_id === floorId);
  };

  // Get space type name
  const getSpaceTypeName = masterSpaceId => {
    const type = spaceTypes?.find(t => t.id === masterSpaceId);
    return type?.name || '';
  };

  // Handle user selecting a space type for an unmatched space
  const handleClassifyUnmatched = (unmatchedId, masterSpaceId) => {
    setUnmatchedSpaces(prev =>
      prev.map(u => (u.id === unmatchedId ? { ...u, selectedMasterSpaceId: masterSpaceId } : u)),
    );
  };

  // Save a classified unmatched space to DB
  const saveClassifiedSpace = async unmatchedSpace => {
    if (!unmatchedSpace.selectedMasterSpaceId) return;

    setIsSaving(true);
    setLocalError(null);

    try {
      const spaceData = {
        floor_id: unmatchedSpace.dbFloorId,
        master_space_id: unmatchedSpace.selectedMasterSpaceId,
        custom_space_name: unmatchedSpace.suggested_name_he || null,
        room_size: unmatchedSpace.estimated_size_sqm || null,
        room_height: null,
        description: null,
      };

      const { data: newSpace, error } = await supabase
        .from(TABLES.PROJECT_SPACES)
        .insert(spaceData)
        .select()
        .single();

      if (error) throw error;

      // Add to local spaces state
      const localId = `space-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setSpaces(prev => [
        ...prev,
        {
          id: localId,
          dbId: newSpace.id,
          floor_id: unmatchedSpace.localFloorId,
          master_space_id: newSpace.master_space_id,
          custom_space_name: newSpace.custom_space_name || '',
          room_size: newSpace.room_size,
          room_height: newSpace.room_height,
          description: newSpace.description || '',
        },
      ]);

      // Remove from unmatched list
      setUnmatchedSpaces(prev => prev.filter(u => u.id !== unmatchedSpace.id));
    } catch (err) {
      logError(err, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA,
        message: 'Failed to save classified space',
        metadata: { projectId, unmatchedId: unmatchedSpace.id },
      });
      setLocalError('שגיאה בשמירת החלל');
    } finally {
      setIsSaving(false);
    }
  };

  // Dismiss an unmatched space (user decides it's not a real space)
  const dismissUnmatched = unmatchedId => {
    setUnmatchedSpaces(prev => prev.filter(u => u.id !== unmatchedId));
  };

  // Toggle floor accordion
  const toggleFloor = floorId => {
    setExpandedFloors(prev => ({
      ...prev,
      [floorId]: !prev[floorId],
    }));
  };

  // Start adding a new space to a floor
  const startAddSpace = floorId => {
    const newSpace = createEmptySpace(floorId);
    setEditingSpace({ floorId, isNew: true });
    setFormData(newSpace);
  };

  // Start editing an existing space
  const startEditSpace = space => {
    setEditingSpace({ floorId: space.floor_id, isNew: false, spaceId: space.id });
    setFormData({ ...space });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingSpace(null);
    setFormData(null);
  };

  // Save space (add or update) - saves to DB immediately
  const saveSpace = async () => {
    if (!formData || !formData.master_space_id) return;

    setIsSaving(true);
    setLocalError(null);

    try {
      // Get the actual DB floor_id from savedFloorIds mapping
      const dbFloorId = savedFloorIds[formData.floor_id];

      if (!dbFloorId && projectId) {
        // If we don't have the mapping, we can't save to DB yet
        // Just save locally for now
        if (editingSpace.isNew) {
          setSpaces(prev => [...prev, formData]);
        } else {
          setSpaces(prev => prev.map(s => (s.id === formData.id ? formData : s)));
        }
        cancelEdit();
        return;
      }

      const spaceData = {
        floor_id: dbFloorId,
        master_space_id: formData.master_space_id,
        custom_space_name: formData.custom_space_name || null,
        room_size: formData.room_size || null,
        room_height: formData.room_height || null,
        description: formData.description || null,
      };

      if (editingSpace.isNew) {
        // Insert new space
        const { data: newSpace, error } = await supabase
          .from(TABLES.PROJECT_SPACES)
          .insert(spaceData)
          .select()
          .single();

        if (error) throw error;

        // Update local state with DB id
        const savedSpace = { ...formData, id: newSpace.id, dbId: newSpace.id };
        setSpaces(prev => [...prev, savedSpace]);

        // If addDoor is checked, create a דלת פנים project_item for the new space
        if (addDoor && projectId) {
          try {
            await createAutoProjectItem(supabase, {
              projectId,
              masterItemId: MASTER_ITEM_IDS.DOOR,
              spaceId: newSpace.id,
            });
          } catch (doorError) {
            // Don't block space creation if door creation fails
            console.error('Error adding door to space:', doorError);
          }
        }

        // If addCeilingGeves is checked, create a תקרת חיפוי גבס project_item
        if (addCeilingGeves && projectId) {
          try {
            await autoCreateCeilingGeves(supabase, {
              projectId,
              spaceId: newSpace.id,
              quantity: ceilingGevesQty || 1,
            });
          } catch (gevesError) {
            console.error('Error adding ceiling geves to space:', gevesError);
          }
        }
      } else {
        // Update existing space
        if (formData.dbId) {
          const { error } = await supabase
            .from(TABLES.PROJECT_SPACES)
            .update(spaceData)
            .eq('id', formData.dbId);

          if (error) throw error;

          // If addCeilingGeves is checked on edit, create with dedup guard
          if (addCeilingGeves && projectId) {
            try {
              await autoCreateCeilingGeves(supabase, {
                projectId,
                spaceId: formData.dbId,
                quantity: ceilingGevesQty || 1,
              });
            } catch (gevesError) {
              console.error('Error adding ceiling geves to space:', gevesError);
            }
          }
        }
        setSpaces(prev => prev.map(s => (s.id === formData.id ? formData : s)));
      }

      cancelEdit();
    } catch (err) {
      logError(err, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA,
        message: 'Failed to save space in wizard',
        metadata: { projectId, floorId: formData?.floor_id },
      });
      setLocalError(err.message || 'שגיאה בשמירת החלל');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete a space
  const deleteSpace = async (spaceId, dbId) => {
    try {
      // Delete from DB if it has a dbId
      if (dbId) {
        const { error } = await supabase.from(TABLES.PROJECT_SPACES).delete().eq('id', dbId);

        if (error) throw error;
      }

      setSpaces(prev => prev.filter(s => s.id !== spaceId));
    } catch (err) {
      logError(err, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA,
        message: 'Failed to delete space in wizard',
        metadata: { projectId, spaceId, dbId },
      });
      setLocalError(err.message || 'שגיאה במחיקת החלל');
    }
  };

  // Get ceiling height for a floor (from wizard floorsData)
  const getFloorCeilingHeight = floorId => {
    const floor = floors.find(f => f.id === floorId);
    return floor?.ceilingHeight || null;
  };

  // Update form field
  const updateFormField = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-suggest ceiling geves when room_height < floor ceilingHeight
      if (field === 'room_height' || field === 'room_size') {
        const floorHeight = getFloorCeilingHeight(updated.floor_id);
        const roomHeight = field === 'room_height' ? value : updated.room_height;
        const roomSize = field === 'room_size' ? value : updated.room_size;
        const shouldSuggest = floorHeight && roomHeight && roomHeight < floorHeight;
        setAddCeilingGeves(shouldSuggest);
        if (shouldSuggest && roomSize) {
          setCeilingGevesQty(roomSize);
        }
      }

      return updated;
    });
    // Auto-update addDoor checkbox when space type changes (new spaces only)
    if (field === 'master_space_id' && editingSpace?.isNew) {
      setAddDoor(spaceHasDoor(value));
    }
  };

  // Check if space type is outdoor
  const isOutdoorSpace = masterSpaceId => {
    const type = spaceTypes?.find(t => t.id === masterSpaceId);
    return type?.space_category === 'חוץ';
  };

  // Check if space type has a door
  const spaceHasDoor = masterSpaceId => {
    const type = spaceTypes?.find(t => t.id === masterSpaceId);
    return !!type?.has_door;
  };

  const floors = floorsData?.floors || [];

  // Split space types into indoor and outdoor groups for categorized dropdowns
  const indoorTypes = spaceTypes?.filter(t => t.space_category !== 'חוץ') || [];
  const outdoorTypes = spaceTypes?.filter(t => t.space_category === 'חוץ') || [];

  // Calculate total sqm across all spaces
  const totalSqm = spaces.reduce((sum, s) => sum + (s.room_size || 0), 0);

  return (
    <div className={styles.stepContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>חללים</h1>
        <p className={styles.subtitle}>הוסף חללים לכל קומה</p>
        <p className={spacesStyles.reassurance}>אל דאגה, תוכל להוסיף ולערוך חללים בכל שלב</p>
      </div>

      {/* Error Message */}
      {localError && <div className={spacesStyles.errorMessage}>{localError}</div>}

      {/* Unmatched Spaces - User Classification */}
      {unmatchedSpaces.length > 0 && (
        <div className={spacesStyles.unmatchedSection}>
          <div className={spacesStyles.unmatchedHeader}>
            <span className={spacesStyles.unmatchedIcon}>?</span>
            <span>זוהו {unmatchedSpaces.length} חללים נוספים — עזור לנו לזהות אותם:</span>
          </div>
          <div className={spacesStyles.unmatchedList}>
            {unmatchedSpaces.map(unmatched => (
              <div key={unmatched.id} className={spacesStyles.unmatchedRow}>
                <div className={spacesStyles.unmatchedInfo}>
                  <span className={spacesStyles.unmatchedName}>
                    {unmatched.suggested_name_he || 'חלל לא מזוהה'}
                  </span>
                  {unmatched.estimated_size_sqm && (
                    <span className={spacesStyles.unmatchedSize}>
                      ~{unmatched.estimated_size_sqm} מ&quot;ר
                    </span>
                  )}
                </div>
                <div className={spacesStyles.unmatchedActions}>
                  <select
                    className={spacesStyles.unmatchedSelect}
                    value={unmatched.selectedMasterSpaceId}
                    onChange={e => handleClassifyUnmatched(unmatched.id, e.target.value)}
                  >
                    <option value="">בחר סוג חלל</option>
                    {indoorTypes.length > 0 && (
                      <optgroup label="פנים">
                        {indoorTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {outdoorTypes.length > 0 && (
                      <optgroup label="חוץ">
                        {outdoorTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <button
                    type="button"
                    className={spacesStyles.unmatchedSave}
                    onClick={() => saveClassifiedSpace(unmatched)}
                    disabled={!unmatched.selectedMasterSpaceId || isSaving}
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    className={spacesStyles.unmatchedDismiss}
                    onClick={() => dismissUnmatched(unmatched.id)}
                    title="לא חלל אמיתי"
                  >
                    ✗
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floors Accordion */}
      <div className={spacesStyles.floorsContainer}>
        {floors.length === 0 ? (
          <div className={spacesStyles.emptyState}>
            <p>לא הוגדרו קומות. חזור לשלב הקודם להגדיר קומות.</p>
          </div>
        ) : (
          floors.map(floor => {
            const floorSpaces = getSpacesForFloor(floor.id);
            const isExpanded = expandedFloors[floor.id];
            const isEditingThisFloor = editingSpace?.floorId === floor.id;

            return (
              <div key={floor.id} className={spacesStyles.floorSection}>
                {/* Floor Header */}
                <div className={spacesStyles.floorHeader} onClick={() => toggleFloor(floor.id)}>
                  <div className={spacesStyles.floorHeaderContent}>
                    <span className={spacesStyles.floorName}>
                      {floor.name || formatFloorName(floor.floorNum)}
                    </span>
                    <span className={spacesStyles.spaceCount}>{floorSpaces.length} חללים</span>
                  </div>
                  <span
                    className={`${spacesStyles.chevron} ${isExpanded ? spacesStyles.expanded : ''}`}
                  >
                    ▼
                  </span>
                </div>

                {/* Floor Content */}
                {isExpanded && (
                  <div className={spacesStyles.floorContent}>
                    {/* Existing Spaces */}
                    <div className={spacesStyles.spacesGrid}>
                      {floorSpaces.map(space => (
                        <div
                          key={space.id}
                          className={spacesStyles.spaceCard}
                          onClick={() => startEditSpace(space)}
                        >
                          <div className={spacesStyles.spaceCardHeader}>
                            <span className={spacesStyles.spaceName}>
                              {space.custom_space_name ||
                                getSpaceTypeName(space.master_space_id) ||
                                'חלל'}
                            </span>
                            <button
                              type="button"
                              className={spacesStyles.deleteButton}
                              onClick={e => {
                                e.stopPropagation();
                                deleteSpace(space.id, space.dbId);
                              }}
                              title="מחק"
                            >
                              🗑
                            </button>
                          </div>
                          <div className={spacesStyles.spaceCardTags}>
                            {space.master_space_id && (
                              <span className={spacesStyles.tag}>
                                {getSpaceTypeName(space.master_space_id)}
                              </span>
                            )}
                            {space.room_size && (
                              <span className={spacesStyles.tag}>{space.room_size} מ&quot;ר</span>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Add Space Button */}
                      {!isEditingThisFloor && (
                        <button
                          type="button"
                          className={spacesStyles.addSpaceCard}
                          onClick={() => startAddSpace(floor.id)}
                        >
                          <span className={spacesStyles.addIcon}>+</span>
                          <span>הוסף חלל</span>
                        </button>
                      )}
                    </div>

                    {/* Inline Edit Form */}
                    {isEditingThisFloor && formData && (
                      <div className={spacesStyles.editForm}>
                        <div className={spacesStyles.formGrid}>
                          {/* Space Type */}
                          <div className={spacesStyles.formField}>
                            <label>סוג חלל</label>
                            <select
                              value={formData.master_space_id}
                              onChange={e => updateFormField('master_space_id', e.target.value)}
                            >
                              <option value="">בחר סוג</option>
                              {indoorTypes.length > 0 && (
                                <optgroup label="פנים">
                                  {indoorTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                      {type.name}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              {outdoorTypes.length > 0 && (
                                <optgroup label="חוץ">
                                  {outdoorTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                      {type.name}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                          </div>

                          {/* Custom Name */}
                          <div className={spacesStyles.formField}>
                            <label>
                              שם מותאם <span className={spacesStyles.optional}>(אופציונלי)</span>
                            </label>
                            <input
                              type="text"
                              value={formData.custom_space_name}
                              onChange={e => updateFormField('custom_space_name', e.target.value)}
                              placeholder="למשל: חדר שינה הורים"
                            />
                          </div>

                          {/* Room Size */}
                          <div className={spacesStyles.formField}>
                            <label>
                              שטח (מ&quot;ר){' '}
                              <span className={spacesStyles.optional}>(אופציונלי)</span>
                            </label>
                            <input
                              type="number"
                              value={formData.room_size || ''}
                              onChange={e =>
                                updateFormField(
                                  'room_size',
                                  e.target.value ? parseFloat(e.target.value) : null,
                                )
                              }
                              min="0"
                              step="0.1"
                              placeholder="12.5"
                            />
                          </div>

                          {/* Room Height - hidden for outdoor */}
                          {!isOutdoorSpace(formData.master_space_id) && (
                            <div className={spacesStyles.formField}>
                              <label>
                                גובה (מ&apos;){' '}
                                <span className={spacesStyles.optional}>(אופציונלי)</span>
                              </label>
                              <input
                                type="number"
                                value={formData.room_height || ''}
                                onChange={e =>
                                  updateFormField(
                                    'room_height',
                                    e.target.value ? parseFloat(e.target.value) : null,
                                  )
                                }
                                min="0"
                                step="0.1"
                                placeholder="2.7"
                              />
                            </div>
                          )}

                          {/* Description */}
                          <div className={`${spacesStyles.formField} ${spacesStyles.fullWidth}`}>
                            <label>
                              תיאור <span className={spacesStyles.optional}>(אופציונלי)</span>
                            </label>
                            <textarea
                              value={formData.description}
                              onChange={e => updateFormField('description', e.target.value)}
                              rows={2}
                              placeholder="הערות נוספות..."
                            />
                          </div>

                          {/* Ceiling geves suggestion - when room height < floor ceiling height */}
                          {addCeilingGeves && !isOutdoorSpace(formData.master_space_id) && (
                            <div className={`${spacesStyles.formField} ${spacesStyles.fullWidth}`}>
                              <label className={spacesStyles.checkboxLabel}>
                                <input
                                  type="checkbox"
                                  checked={addCeilingGeves}
                                  onChange={e => setAddCeilingGeves(e.target.checked)}
                                />
                                <span>הוסף תקרת גבס</span>
                              </label>
                              {addCeilingGeves && (
                                <div className={spacesStyles.gevesQtyRow}>
                                  <label className={spacesStyles.gevesQtyLabel}>
                                    כמות (מ&quot;ר):
                                  </label>
                                  <input
                                    type="number"
                                    className={spacesStyles.gevesQtyInput}
                                    value={ceilingGevesQty || ''}
                                    onChange={e =>
                                      setCeilingGevesQty(
                                        e.target.value ? parseFloat(e.target.value) : null,
                                      )
                                    }
                                    min="0"
                                    step="0.1"
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Indoor door checkbox - only for new spaces with has_door space type */}
                          {editingSpace?.isNew && spaceHasDoor(formData.master_space_id) && (
                            <div className={`${spacesStyles.formField} ${spacesStyles.fullWidth}`}>
                              <label className={spacesStyles.checkboxLabel}>
                                <input
                                  type="checkbox"
                                  checked={addDoor}
                                  onChange={e => setAddDoor(e.target.checked)}
                                />
                                <span>הוסף דלת פנים לחלל</span>
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Form Actions */}
                        <div className={spacesStyles.formActions}>
                          <button
                            type="button"
                            className={spacesStyles.cancelButton}
                            onClick={cancelEdit}
                          >
                            ביטול
                          </button>
                          <button
                            type="button"
                            className={spacesStyles.saveButton}
                            onClick={saveSpace}
                            disabled={!formData.master_space_id || isSaving}
                          >
                            {isSaving ? 'שומר...' : editingSpace.isNew ? 'הוסף' : 'שמור'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Total sqm summary */}
      {spaces.length > 0 && (
        <div className={spacesStyles.totalSqm}>
          <span className={spacesStyles.totalSqmLabel}>סה״כ שטח:</span>
          <span className={spacesStyles.totalSqmValue}>
            {totalSqm > 0 ? `${totalSqm.toLocaleString('he-IL')} מ"ר` : 'לא הוזן שטח'}
          </span>
          <span className={spacesStyles.totalSqmCount}>({spaces.length} חללים)</span>
        </div>
      )}
    </div>
  );
};

export default SpacesStep;
